import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCareersClientValue } from "../../App/State/Slices/SelectedCareersClientSlice";
import { selectClientValue } from "../../App/State/Slices/SelectedClientSlice";
import {
  Card,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Stack,
  Theme,
} from "@mui/material";
import LineGraph from "../../App/Components/Graphs/LineGraph";
import { useTheme } from "@mui/styles";
import { convertDateMonth } from "../../App/Helpers/DateConverters";
import { useHistory } from "react-router-dom";
import PieChart from "../../App/Components/Graphs/PieChart";
import { AdGroupType } from "../../App/Enums/AdGroupType";
import { useGetDashboardQuery } from "../../App/Queries/DashboardQueries";
import {
  selectDashboardSettings,
  updateSettings,
} from "../../App/State/Slices/DashboardSettingsSlice";
import { CategoryInfo } from "../../App/Typings/Dashboard";

export default function MainPage() {
  const dispatch = useDispatch();
  const selectCareersClient = useSelector(selectCareersClientValue);
  const selectedClient = useSelector(selectClientValue);
  const dashboardSettings = useSelector(selectDashboardSettings);

  const theme = useTheme() as any;
  const history = useHistory();
  const careersClientId = selectCareersClient?.id;
  const {
    data: dashboard,
    isLoading,
    isError,
    refetch,
  } = useGetDashboardQuery(careersClientId);

  const [alertColors, setAlertColors] = useState<any[]>([]);
  const [alertHighlights, setAlertHighlights] = useState<any[]>([]);
  const [marketingColors, setMarketingColors] = useState<any[]>([]);
  const [marketingHighlights, setMarketingHighlights] = useState<any[]>([]);
  const [crmOnlyClient, setOnlyCrmClient] = useState(false);

  const days = useMemo(
    () => dashboard?.jobAlerts.map((a: any) => convertDateMonth(a.item1)),
    [dashboard]
  );

  const categoryTypes = useMemo(() => {
    return Object.keys(AdGroupType)
      .filter((c) => !isNaN(Number(c)))
      .filter((c) => {
        const inx = Number(c);
        return (
          getCategoryLength(
            dashboard?.jobAlertCategories ?? [],
            inx as AdGroupType
          ) > 0
        );
      })
      .map((i) => Number(i) as AdGroupType);
  }, [dashboard]);

  const categories = useMemo(
    () =>
      dashboard?.jobAlertCategories
        .filter((a) => a.adGroupType === dashboardSettings.groupType)
        .flatMap((c) => c.categories.map((d) => d.item1)),
    [dashboard, dashboardSettings.groupType]
  );

  const marketingData = {
    labels: days,
    datasets: [
      {
        data: dashboard?.marketing.map((a) => a.item2),
        fill: true,
        lineTension: 0.4,
        backgroundColor: theme.palette.primary.graph,
        borderColor: theme.palette.primary.main,
      },
    ],
  };

  const jobAlertData = {
    labels: days,
    datasets: [
      {
        data: dashboard?.jobAlerts.map((a) => a.item2),
        fill: true,
        lineTension: 0.4,
        backgroundColor: theme.palette.primary.graph,
        borderColor: "rgba(102,137,107,1)",
      },
    ],
  };

  const marketingSignups = {
    labels: categories,
    datasets: [
      {
        backgroundColor: marketingColors,
        hoverBackgroundColor: marketingHighlights,
        data: dashboard?.marketingCategories
          .filter((c) => c.adGroupType === dashboardSettings.groupType)
          .flatMap((a) => a.categories.map((d) => d.item2)),
      },
    ],
  };

  const jobSignups = {
    labels: categories,
    datasets: [
      {
        backgroundColor: alertColors,
        hoverBackgroundColor: alertHighlights,
        data: dashboard?.jobAlertCategories
          .filter((c) => c.adGroupType === dashboardSettings.groupType)
          .flatMap((a) => a.categories.map((d) => d.item2)),
      },
    ],
  };

  async function generateColors(
    setColor: (color: any[]) => void,
    setHighlight: (color: any[]) => void,
    values: any[]
  ) {
    var chartColorsTemp = [];
    var chartHighlightsTemp = [];

    var priority = ["#0a6e33", "#2f8754", "#00af4a"];

    var priorityHighlights = ["#1E8247", "#439B68", "#14C35E"];

    for (var i = 0; i < values.length; i++) {
      if (values[i] > 0 && priority.length > 0) {
        var color = priority.pop();
        var highlight = priorityHighlights.pop();
        chartColorsTemp.push(color);
        chartHighlightsTemp.push(highlight);
      } else {
        var r = Math.floor(Math.random() * 200);
        var g = Math.floor(Math.random() * 200);
        var b = Math.floor(Math.random() * 200);
        var c = "rgb(" + r + ", " + g + ", " + b + ")";
        var h = "rgb(" + (r + 20) + ", " + (g + 20) + ", " + (b + 20) + ")";
        chartColorsTemp.push(c);
        chartHighlightsTemp.push(h);
      }
    }

    setColor(chartColorsTemp);
    setHighlight(chartHighlightsTemp);
  }

  useEffect(() => {
    if (dashboard && !isLoading) {
      generateColors(
        setAlertColors,
        setAlertHighlights,
        dashboard?.jobAlertCategories
          .filter((d) => d.adGroupType === dashboardSettings.groupType)
          .flatMap((d) => d.categories.map((a) => a.item2)) ?? []
      );
      generateColors(
        setMarketingColors,
        setMarketingHighlights,
        dashboard?.marketingCategories
          .filter((d) => d.adGroupType === dashboardSettings.groupType)
          .flatMap((d) => d.categories.map((a) => a.item2)) ?? []
      );
    }
  }, [dashboard, isLoading, dashboardSettings.groupType]);

  useEffect(() => {
    if (selectCareersClient) {
    } else {
      setOnlyCrmClient(true);
    }
  }, [selectCareersClient]);

  // Without a careers client there is no dashboard data to fetch (the query is
  // disabled). Show an explicit message rather than skeletons that never
  // resolve. A CRM-only client (no careers) legitimately has no dashboard; a
  // careers-enabled client only lands here transiently while the careers client
  // loads, or if that lookup failed — so don't wrongly claim it has no careers
  // configuration in that case.
  if (!careersClientId) {
    return selectedClient?.hasCareers ? (
      <DashboardMessage
        title="Dashboard data unavailable"
        message="We couldn't load the dashboard for this client. Try reselecting the tenant from the profile menu, or reload the page."
      />
    ) : (
      <DashboardMessage
        title="No dashboard data for this client"
        message="This client doesn't have a careers configuration, so dashboard metrics aren't available."
      />
    );
  }

  if (isError) {
    return (
      <DashboardMessage
        title="We couldn't load the dashboard"
        message="Something went wrong while loading your dashboard data. Please try again."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        width: "100%",
        mt: 12,
        height: "calc(100vh - 140px)",
        overflowY: "auto",
      }}
    >
      <Grid
        container
        columnSpacing={4}
        rowSpacing={2}
        sx={{ minHeight: "100%" }}
      >
        <Grid item xs={12} sm={12} md={12} lg={10}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="adgroup-type-dropdown-label">
                  Group Type
                </InputLabel>
                {categoryTypes.length > 0 && (
                  <Select
                    labelId="adgroup-type-dropdown-label"
                    id="adgroup-type-dropdown"
                    value={
                      categoryTypes.includes(
                        dashboardSettings.groupType as AdGroupType
                      )
                        ? dashboardSettings.groupType
                        : categoryTypes[0]
                    }
                    label="Group Type"
                    data-test="group-type-dropdown"
                    onChange={(ev) =>
                      dispatch(
                        updateSettings({
                          groupType: parseInt(ev.target.value.toString()),
                        })
                      )
                    }
                  >
                    {categoryTypes.map((c) => {
                      return (
                        <MenuItem key={`category-${c}`} value={Number(c)}>
                          {AdGroupType[c].toString()}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </FormControl>
            </Grid>
            {(categories?.length ?? 0) > 0 ? (
              <>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      height: 650,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onClick={() => history.push("/jobalertsreport")}
                    data-test="monthly-job-alert-signups-pie"
                  >
                    <Box
                      sx={{
                        height: "90%",
                        width: "90%",
                        minHeight: 200,
                        minWidth: 200,
                      }}
                    >
                      <PieChart
                        data={jobSignups}
                        title="Monthly Job Alert Signups"
                        loading={isLoading}
                      />
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      height: 650,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onClick={() => history.push("/marketreport")}
                    data-test="monthly-signups-pie"
                  >
                    <Box
                      sx={{
                        height: "90%",
                        width: "90%",
                        minHeight: 200,
                        minWidth: 200,
                      }}
                    >
                      <PieChart
                        data={marketingSignups}
                        title="Monthly Marketing Signups"
                        loading={isLoading}
                      />
                    </Box>
                  </Card>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Card
                    sx={{ height: 350 }}
                    onClick={() => history.push("/marketreport")}
                  >
                    <LineGraph
                      data={marketingData}
                      title="Marketing Signups"
                      loading={isLoading}
                    />
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card
                    sx={{ height: 350 }}
                    onClick={() => history.push("/jobalertsreport")}
                  >
                    <LineGraph
                      data={jobAlertData}
                      title="Job Alert Signups"
                      loading={isLoading}
                    />
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={2}>
          <Stack
            direction={{ md: "row", lg: "column" }}
            spacing={{ md: 0, lg: 2 }}
            columnGap={1}
            flexWrap="wrap"
            rowGap={1}
            justifyContent={"flex-start"}
          >
            <NumberCard
              title={"Featured Jobs"}
              action={() => history.push("/featuredjobs")}
              number={dashboard?.featuredJobs ?? 0}
              theme={theme}
              testId="featured-jobs-card"
            />
            <NumberCard
              title={"Featured Job Groups"}
              action={() => history.push("/featuredgroups")}
              number={dashboard?.featuredJobGroups ?? 0}
              theme={theme}
              testId="featured-job-groups-card"
            />
            <NumberCard
              title={"Upcoming Events"}
              action={() => history.push("/events")}
              number={dashboard?.events ?? 0}
              theme={theme}
              testId="upcoming-events-card"
            />
            <NumberCard
              title={"Monthly Job Alerts"}
              action={() => history.push("/jobalertsreport")}
              number={dashboard?.totalJobAlerts ?? 0}
              theme={theme}
              testId="monthly-job-alerts-card"
            />
            <NumberCard
              title={"Monthly Marketing"}
              action={() => history.push("/marketreport")}
              number={dashboard?.totalMarketing ?? 0}
              theme={theme}
              testId="monthly-marketing-card"
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

function NumberCard({
  title,
  number,
  action,
  theme,
  testId,
}: {
  title: string;
  number: number;
  action: () => void;
  theme: Theme;
  testId: string;
}) {
  return (
    <Card
      data-test={testId}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        height: 126,
        alignItems: "center",
        fontSize: { xs: "22px", md: "18px", xl: "22px" },
        backgroundColor: theme.palette.primary.main,
        color: "white",
        minWidth: 200,
        transition: ".2s",
        md: {
          width: "90%",
          padding: 1,
          minHeight: 80,
        },
        "&:hover": {
          background: theme.palette.primary.greenHighlight,
        },
      }}
      onClick={() => action()}
    >
      <span>{title}</span>
      <Typography sx={{ fontWeight: "bold", fontSize: "34px" }}>
        {number}
      </Typography>
    </Card>
  );
}

const getCategoryLength = (categoryInfo: CategoryInfo[], type: AdGroupType) => {
  return (
    categoryInfo.find((c) => c.adGroupType === type)?.categories.length ?? 0
  );
};

function DashboardMessage({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Box
      data-test="dashboard-message"
      sx={{
        mt: 12,
        p: 3,
        width: "100%",
        height: "calc(100vh - 140px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        textAlign: "center",
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 440 }}>
        {message}
      </Typography>
      {onRetry && (
        <Button variant="contained" onClick={onRetry} data-test="dashboard-retry">
          Retry
        </Button>
      )}
    </Box>
  );
}
